import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { hiringPeopleAPI, type HiringJoiner, type HiringJoiners, type HiringLink, type HiringPlaced } from '../../services/api';
import {
  HIRING_CODES,
  PEOPLE_CODES,
  PEOPLE_WORDS,
  asksWhoJoined,
  hiringCodeOf,
  hiringErrorText,
  hiringStatusOf,
  isHiringOff,
  joinerMatchWords,
  joinerWhereLine,
  personWords,
} from './hiringText';

/**
 * "Who joined", on a card whose opening reads "Joined, not yet at this store".
 *
 * Recruitment's numbers say that someone joined on the opening; they do not say who. This
 * box asks the server who, on the project's employee list, that may be, and shows each
 * person with how they were matched - so it is plain that a match is a proposal a person
 * confirms, not a fact. A person who may manage hiring and looks after the store places
 * one of them: two clicks, and the server then deploys the person at the store (as
 * "Assign team" on the Stores page would) and closes the hiring as filled, together.
 *
 * Nothing here is sent to Recruitment. The people listed are employees, shown by code and
 * name as the Stores page shows a store's team; no candidate is listed.
 *
 * What is confirmed is what was shown: a placing brings back the token its person came
 * with. When something about them changed meanwhile the server refuses, and the list is
 * read again.
 *
 * A link that is no longer followed (someone else stopped, cancelled or closed it first -
 * or placed its joiner) is the card's to show (`onLinkGone`), exactly as it shows one that
 * "Stop following" found already stopped: the owner's re-read drops the link, and the card
 * must not go with it.
 *
 * A person without Hiring (manage) gets one line and no request: the list is asked for
 * only by someone who could act on it.
 */

const BOX = 'space-y-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900';
const BUTTON = 'inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold transition-colors disabled:opacity-50';
const PRIMARY = `${BUTTON} bg-emerald-700 text-white hover:bg-emerald-800`;
const QUIET = `${BUTTON} border border-emerald-300 bg-white text-emerald-900 hover:bg-emerald-100`;

type Read = { state: 'loading' } | { state: 'ready'; answer: HiringJoiners } | { state: 'failed' } | { state: 'gone' };

export function HiringJoinerBox({
  projectId, link, canManage, onPlaced, onOutOfDate, onLinkGone, onHiringGone,
}: {
  projectId: number;
  link: HiringLink;
  canManage: boolean;
  /** A placing went through: the answer, with the link as it now stands (closed as filled). */
  onPlaced: (result: HiringPlaced) => void;
  /** The owner's list no longer shows this link as it is: it is to be read again. */
  onOutOfDate: () => void;
  /**
   * The link is no longer followed (someone stopped, cancelled or closed it first). The
   * card says so, in `sentence`, and keeps itself on show. Where it is not given, the list
   * is read again (`onOutOfDate`) and a refused placing says the sentence here.
   */
  onLinkGone?: (sentence: string) => void;
  /** Hiring was switched off for the project while the panel was open. */
  onHiringGone: () => void;
}) {
  const shown = asksWhoJoined(link);
  const asking = shown && canManage;
  const [read, setRead] = useState<Read>({ state: 'loading' });
  const [reads, setReads] = useState(0);
  // The person whose question is open: the first click only asks.
  const [confirming, setConfirming] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // What a placing did, said once it went through: the card is closed by then.
  const [done, setDone] = useState<string | null>(null);
  const alive = useRef(true);
  // The owner's callbacks as they are now, without making the list re-read on each render.
  const gone = useRef(onHiringGone);
  gone.current = onHiringGone;
  const outOfDate = useRef(onOutOfDate);
  outOfDate.current = onOutOfDate;
  const linkGone = useRef(onLinkGone);
  linkGone.current = onLinkGone;

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    if (!asking) return undefined;
    // An answer for a box that has since closed, or asked again, is dropped.
    let cancelled = false;
    setRead({ state: 'loading' });
    Promise.resolve()
      .then(() => hiringPeopleAPI.joiners(projectId, link.id))
      .then((res) => {
        if (cancelled) return;
        const answer = res?.data?.data;
        if (!answer || !Array.isArray(answer.people)) throw new Error('unreadable answer');
        setRead({ state: 'ready', answer });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (isHiringOff(err)) {
          gone.current();
          return;
        }
        // Stopped, or placed by someone else meanwhile: the card says so and stays on show
        // where it can; otherwise the page's own list, read again, says what is true now.
        const code = hiringCodeOf(err);
        if (code === HIRING_CODES.linkNotLive || code === HIRING_CODES.linkNotFound) {
          if (code === HIRING_CODES.linkNotLive && linkGone.current) linkGone.current(hiringErrorText(err, PEOPLE_WORDS.joinerFailed));
          else outOfDate.current();
          setRead({ state: 'gone' });
          return;
        }
        setRead({ state: 'failed' });
      });
    return () => {
      cancelled = true;
    };
  }, [asking, projectId, link.id, reads]);

  const place = async (person: HiringJoiner) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await hiringPeopleAPI.place(projectId, link.id, { empCode: person.empCode, token: person.token });
      const result = res?.data?.data;
      if (!result?.link) throw new Error('unreadable answer');
      // The owner is told whether or not this box is still on show.
      onPlaced(result);
      if (!alive.current) return;
      setConfirming(null);
      setDone(result.message);
    } catch (err) {
      if (isHiringOff(err)) {
        onHiringGone();
        return;
      }
      const code = hiringCodeOf(err);
      // Someone else ended the link first: the card is the one to say so, and to stay on show.
      if (code === HIRING_CODES.linkNotLive && onLinkGone) {
        onLinkGone(hiringErrorText(err, PEOPLE_WORDS.placeNoAnswer));
        if (alive.current) setConfirming(null);
        return;
      }
      // The link was stopped or closed meanwhile: the page's list is behind.
      if (code === HIRING_CODES.linkNotLive || code === HIRING_CODES.linkNotFound) onOutOfDate();
      // No answer at all: whether the person was placed is not known, so the page's list is read again too.
      if (hiringStatusOf(err) === undefined) onOutOfDate();
      if (!alive.current) return;
      setConfirming(null);
      setError(hiringErrorText(err, PEOPLE_WORDS.placeNoAnswer));
      // The list is what the person acts on next: it is read again whenever it may have moved.
      const listMoved: readonly (string | undefined)[] = [
        PEOPLE_CODES.notAMatch, PEOPLE_CODES.joinersChanged, PEOPLE_CODES.nobodyJoined, PEOPLE_CODES.storeNotYours,
      ];
      if (listMoved.includes(code) || hiringStatusOf(err) === undefined) setReads((n) => n + 1);
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  if (!shown && !done) return null;
  // Stopped or closed by someone else meanwhile: the page's list, read again, says so.
  if (read.state === 'gone' && !done && !error) return null;

  const answer = read.state === 'ready' ? read.answer : null;
  const people = answer?.people ?? [];
  const storeName = answer?.store?.storeName || (link.subject.kind === 'store' ? link.subject.storeName : null) || 'this store';
  // Nobody matched, and it is not that Recruitment named someone who is not on the list yet (the sentence then says why).
  const nobody = !!answer && answer.joined && answer.roster === 'read' && people.length === 0 && !(answer.named && answer.named.onEmployeeList === false);

  return (
    <div role="group" aria-label={PEOPLE_WORDS.joinerHeading} className={BOX}>
      <p className="font-semibold">{PEOPLE_WORDS.joinerHeading}</p>
      {done && <p role="status" className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800">{done}</p>}
      {error && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">{error}</p>}

      {shown && !canManage && <p>{PEOPLE_WORDS.joinerCannotPlace}</p>}

      {asking && read.state === 'loading' && <p>{PEOPLE_WORDS.joinerLoading}</p>}
      {asking && read.state === 'failed' && (
        <div className="flex flex-wrap items-center gap-2">
          <p>{PEOPLE_WORDS.joinerFailed}</p>
          <button type="button" className={QUIET} onClick={() => setReads((n) => n + 1)}>{PEOPLE_WORDS.tryAgain}</button>
        </div>
      )}

      {asking && answer && (
        <>
          <p>{answer.sentence}</p>
          {people.length > 0 && (
            <ul className="space-y-2">
              {people.map((person) => {
                const match = joinerMatchWords(person.match);
                const joined = PEOPLE_WORDS.joinedOn(person.dateOfJoining);
                const who = personWords(person);
                return (
                  <li key={person.empCode} aria-label={who} className="space-y-1 rounded-lg border border-emerald-200 bg-white px-3 py-2 text-gray-800">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-medium text-gray-900">{who}</p>
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-800">{match.tag}</span>
                    </div>
                    {joined && <p>{joined}</p>}
                    {match.sentence && <p>{match.sentence}</p>}
                    <p>{joinerWhereLine(person)}</p>
                    {/* Two clicks, as Remove on a store's team: the first only asks. */}
                    {answer.canPlace && (
                      confirming === person.empCode ? (
                        <div role="group" aria-label={`Confirm for ${who}`} className="space-y-2 border-t border-gray-100 pt-2">
                          <p className="font-medium text-gray-800">
                            {person.atThisStore ? PEOPLE_WORDS.closeQuestion(person) : PEOPLE_WORDS.placeQuestion(person, storeName)}
                          </p>
                          <div className="flex items-center gap-2">
                            {/* Focus lands on the safe answer: the button that was clicked has just left the page. */}
                            <button type="button" autoFocus disabled={busy} className={QUIET} onClick={() => setConfirming(null)}>
                              {PEOPLE_WORDS.goBack}
                            </button>
                            <button type="button" disabled={busy} className={PRIMARY} onClick={() => void place(person)}>
                              {busy && <Loader2 className="h-3 w-3 animate-spin" />}
                              {person.atThisStore ? PEOPLE_WORDS.yesClose : PEOPLE_WORDS.yesPlace}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="pt-1">
                          <button
                            type="button"
                            disabled={busy || confirming !== null}
                            className={PRIMARY}
                            // Several people can be listed: each button says whom it is for.
                            aria-label={`${person.atThisStore ? PEOPLE_WORDS.closeAsFilled : PEOPLE_WORDS.place}: ${who}`}
                            onClick={() => {
                              setError(null);
                              setConfirming(person.empCode);
                            }}
                          >
                            {person.atThisStore ? PEOPLE_WORDS.closeAsFilled : PEOPLE_WORDS.place}
                          </button>
                        </div>
                      )
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {nobody && PEOPLE_WORDS.nobodyHints.map((hint) => <p key={hint}>{hint}</p>)}
          {people.length > 0 && <p className="text-emerald-800">{PEOPLE_WORDS.listFootnote}</p>}
        </>
      )}
    </div>
  );
}

export default HiringJoinerBox;
